import { Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, combineLatestWith, map, Subscription } from 'rxjs';
import { parseNumberFromString } from '../utils/validation-helpers';

/**
 * Directive for managing a numeric input field control. Can be used both for input[type=text] and input[type=number] fields.
 * Can be used with mat-input
 * @remarks When using the input, make sure not to directly bind a FormControl to the input, but instead use the [numberFormControl]
 * attribute instead.
 * @example ```html
 * <input type="number" appNumberInput
 *                      [numberFormControl]="someFormControl"
 *                      [numberOfDecimals]="2" />
 * ```
 */
@Directive({
  selector: '[appNumberInput]',
  standalone: false
})
export class NumberInputDirective implements OnInit, OnChanges, OnDestroy {
  /**
   * The form control to manage and sync with the input field
   */
  @Input() numberFormControl: FormControl<number>;

  /**
   * Number of decimals to use when handling the number.
   * The number will be rounded to and displayed with this amount of decimals
   */
  @Input() numberOfDecimals?: number;

  /**
   * Locale code to use when formatting the number
   */
  @Input() localeCode: string;

  constructor(private el: ElementRef<HTMLInputElement>) {}

  private valueChangedSubscription: Subscription;
  private stepSubscription: Subscription;

  private decimalsSubject = new BehaviorSubject<number>(0);

  private focused = false;

  @HostListener('blur') onBlur(): void {
    this.focused = false;
    const value = this.el.nativeElement.value;
    const numericValue = parseNumberFromString(value, this.getNumberFormat(this.numberOfDecimals || 0));

    // Value not changed -> no need to do anything (except format the number properly)
    if (numericValue === this.numberFormControl.value && !this.focused) {
      this.updateInputValue(numericValue);
      return;
    }

    // Update validation status, if needed
    if (!Number.isNaN(numericValue)) {
      // Number is valid -> update form control
      this.numberFormControl.setValue(numericValue);
      this.updateInputValue(numericValue);
    } else {
      this.numberFormControl.setValue(null);
    }
  }

  @HostListener('focus') onFocus(): void {
    this.focused = true;
  }

  ngOnInit(): void {
    this.stepSubscription = this.decimalsSubject
      .asObservable()
      .pipe(
        map(decimals => {
          if (!decimals) {
            return 1;
          }

          return Number.parseFloat(`1e-${decimals}`);
        })
      )
      .subscribe(step => {
        if (this.el.nativeElement.type === 'number') {
          this.el.nativeElement.step = step.toString();
        }
      });
  }

  ngOnDestroy(): void {
    this.valueChangedSubscription?.unsubscribe();
    this.stepSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['numberFormControl']) {
      if (changes['numberFormControl'].isFirstChange()) {
        const numericValue = this.numberFormControl.value;
        if (!Number.isNaN(numericValue)) {
          this.updateInputValue(numericValue);
        }
      }

      this.valueChangedSubscription?.unsubscribe();

      this.numberFormControl.registerOnChange((value: number) => {
        this.updateInputValue(value);
      });

      this.valueChangedSubscription = this.numberFormControl.valueChanges
        .pipe(combineLatestWith(this.decimalsSubject))
        .subscribe(([numericValue]) => {
          if (!this.focused && !Number.isNaN(numericValue)) {
            this.updateInputValue(numericValue);
          }
        });
    }

    if (changes['numberOfDecimals']) {
      this.decimalsSubject.next(changes['numberOfDecimals'].currentValue);
    }
  }

  /**
   * Set the provided number as the value of the input field, with appropriate decimal amount
   * @param value The numeric value
   */
  private updateInputValue(value: number): void {
    if (!Number.isNaN(value) && value !== null) {
      const numberOfDecimals = this.numberOfDecimals || 0;
      const formatter = this.getNumberFormat(numberOfDecimals);
      this.el.nativeElement.value = formatter.format(value);
    }
  }

  private getNumberFormat(decimals: number): Intl.NumberFormat {
    return new Intl.NumberFormat(this.localeCode, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }
}
